/**
 * ATS (Applicant Tracking System) Constants
 * Shared across Job Tracker, Candidate Tracker, New Hires, and FLM
 * Maintains consistency across all Talent Acquisition modules
 */

import { CRM_CONSTANTS } from '../../../HR/CRM/utils/crmConstants';

// ===========================
// JOB TRACKING
// ===========================

export const JOB_STATUS = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  ON_HOLD: 'On Hold',
  FILLED: 'Filled',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled'
};

export const JOB_STATUS_OPTIONS = Object.values(JOB_STATUS);

// ===========================
// PRIORITIES (Uses CRM Constants)
// ===========================

export const PRIORITY = {
  LOW: CRM_CONSTANTS.TICKET_PRIORITY.LOW.value,
  NORMAL: CRM_CONSTANTS.TICKET_PRIORITY.NORMAL.value,
  HIGH: CRM_CONSTANTS.TICKET_PRIORITY.HIGH.value,
  URGENT: CRM_CONSTANTS.TICKET_PRIORITY.URGENT.value
};

export const PRIORITY_OPTIONS = Object.values(PRIORITY);

// Helper to get priority color
export const getPriorityColor = (priority) => {
  const priorityKey = Object.keys(PRIORITY).find(
    key => PRIORITY[key] === priority
  );
  if (!priorityKey) return '#6c757d';
  return CRM_CONSTANTS.TICKET_PRIORITY[priorityKey]?.color || '#6c757d';
};

// ===========================
// DEPARTMENTS
// ===========================

export const DEPARTMENT = {
  CORPORATE: 'Corporate',
  FINANCE: 'Finance',
  HR: 'HR',
  IT: 'IT',
  MANAGEMENT: 'Management',
  MARKETING: 'Marketing',
  OPERATIONS: 'Operations',
  OTHER: 'Other'
};

// Sorted alphabetically
export const DEPARTMENT_OPTIONS = Object.values(DEPARTMENT).sort();

// ===========================
// CANDIDATE STATUS
// ===========================

export const CANDIDATE_STATUS = {
  NEW: 'New',
  SCREENING: 'Screening',
  INTERVIEWING: 'Interviewing',
  OFFER_EXTENDED: 'Offer Extended',
  OFFER_ACCEPTED: 'Offer Accepted',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn'
};

export const CANDIDATE_STATUS_OPTIONS = Object.values(CANDIDATE_STATUS);

export const CANDIDATE_STATUS_COLORS = {
  [CANDIDATE_STATUS.NEW]: '#007bff',
  [CANDIDATE_STATUS.SCREENING]: '#17a2b8',
  [CANDIDATE_STATUS.INTERVIEWING]: '#ffc107',
  [CANDIDATE_STATUS.OFFER_EXTENDED]: '#fd7e14',
  [CANDIDATE_STATUS.OFFER_ACCEPTED]: '#28a745',
  [CANDIDATE_STATUS.HIRED]: '#20c997',
  [CANDIDATE_STATUS.REJECTED]: '#dc3545',
  [CANDIDATE_STATUS.WITHDRAWN]: '#6c757d'
};

// ===========================
// RECRUITMENT SOURCE
// ===========================

export const RECRUITMENT_SOURCE = {
  INTERNAL_REFERRAL: 'Internal Referral',
  EXTERNAL_REFERRAL: 'External Referral',
  JOB_BOARD: 'Job Board',
  LINKEDIN: 'LinkedIn',
  INDEED: 'Indeed',
  COMPANY_WEBSITE: 'Company Website',
  RECRUITER: 'Recruiter',
  CAREER_FAIR: 'Career Fair',
  WALK_IN: 'Walk-in',
  OTHER: 'Other'
};

export const RECRUITMENT_SOURCE_OPTIONS = Object.values(RECRUITMENT_SOURCE);

// ===========================
// INTERVIEW TYPES
// ===========================

export const INTERVIEW_TYPE = {
  PHONE_SCREEN: 'Phone Screen',
  VIDEO_INTERVIEW: 'Video Interview',
  IN_PERSON: 'In-Person',
  PANEL_INTERVIEW: 'Panel Interview',
  TECHNICAL_ASSESSMENT: 'Technical Assessment',
  FINAL_INTERVIEW: 'Final Interview'
};

export const INTERVIEW_TYPE_OPTIONS = Object.values(INTERVIEW_TYPE);

// ===========================
// NEW HIRE STATUS
// ===========================

export const NEW_HIRE_STATUS = {
  PENDING_START: 'Pending Start',
  ONBOARDING: 'Onboarding',
  ACTIVE: 'Active',
  TRAINING: 'Training',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled'
};

export const NEW_HIRE_STATUS_OPTIONS = Object.values(NEW_HIRE_STATUS);

// ===========================
// FLM (First Line Manager) STATUS
// ===========================

export const FLM_STATUS = {
  PENDING_UPLOAD: 'Pending Upload',
  UPLOADED: 'Uploaded',
  IN_REVIEW: 'In Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RESUBMIT: 'Resubmit'
};

export const FLM_STATUS_OPTIONS = Object.values(FLM_STATUS);

// ===========================
// OFFER STATUS
// ===========================

export const OFFER_STATUS = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  EXTENDED: 'Extended',
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  WITHDRAWN: 'Withdrawn'
};

export const OFFER_STATUS_OPTIONS = Object.values(OFFER_STATUS);

// ===========================
// EMPLOYMENT TYPE
// ===========================

export const EMPLOYMENT_TYPE = {
  FULL_TIME: 'Full-Time',
  PART_TIME: 'Part-Time',
  CONTRACT: 'Contract',
  TEMPORARY: 'Temporary',
  SEASONAL: 'Seasonal',
  INTERN: 'Intern'
};

export const EMPLOYMENT_TYPE_OPTIONS = Object.values(EMPLOYMENT_TYPE);

// ===========================
// REJECTION REASONS
// ===========================

export const REJECTION_REASON = {
  QUALIFICATIONS: 'Does not meet qualifications',
  EXPERIENCE: 'Insufficient experience',
  SKILLS: 'Skills mismatch',
  CULTURE_FIT: 'Not a culture fit',
  SALARY_EXPECTATIONS: 'Salary expectations too high',
  BACKGROUND_CHECK: 'Failed background check',
  REFERENCES: 'Poor references',
  POSITION_FILLED: 'Position filled by another candidate',
  WITHDREW: 'Candidate withdrew',
  NO_SHOW: 'No-show to interview',
  OTHER: 'Other'
};

export const REJECTION_REASON_OPTIONS = Object.values(REJECTION_REASON);

// ===========================
// HELPER FUNCTIONS
// ===========================

/**
 * Get candidate status color
 */
export const getCandidateStatusColor = (status) => {
  return CANDIDATE_STATUS_COLORS[status] || '#6c757d';
};

/**
 * Check if a job status is active (can accept candidates)
 */
export const isJobActive = (status) => {
  return status === JOB_STATUS.OPEN || status === JOB_STATUS.IN_PROGRESS;
};

/**
 * Check if a candidate can be assigned to a job
 */
export const canAssignCandidate = (candidateStatus) => {
  return [
    CANDIDATE_STATUS.NEW,
    CANDIDATE_STATUS.SCREENING,
    CANDIDATE_STATUS.INTERVIEWING,
    CANDIDATE_STATUS.OFFER_EXTENDED
  ].includes(candidateStatus);
};

/**
 * Check if a candidate has been hired
 */
export const isCandidateHired = (candidateStatus) => {
  return candidateStatus === CANDIDATE_STATUS.HIRED;
};

/**
 * Get next logical status for candidate progression
 */
export const getNextCandidateStatus = (currentStatus) => {
  const progressionMap = {
    [CANDIDATE_STATUS.NEW]: CANDIDATE_STATUS.SCREENING,
    [CANDIDATE_STATUS.SCREENING]: CANDIDATE_STATUS.INTERVIEWING,
    [CANDIDATE_STATUS.INTERVIEWING]: CANDIDATE_STATUS.OFFER_EXTENDED,
    [CANDIDATE_STATUS.OFFER_EXTENDED]: CANDIDATE_STATUS.OFFER_ACCEPTED,
    [CANDIDATE_STATUS.OFFER_ACCEPTED]: CANDIDATE_STATUS.HIRED
  };
  return progressionMap[currentStatus] || currentStatus;
};

/**
 * Validate priority value
 */
export const isValidPriority = (priority) => {
  return PRIORITY_OPTIONS.includes(priority);
};

/**
 * Validate job status value
 */
export const isValidJobStatus = (status) => {
  return JOB_STATUS_OPTIONS.includes(status);
};

/**
 * Validate candidate status value
 */
export const isValidCandidateStatus = (status) => {
  return CANDIDATE_STATUS_OPTIONS.includes(status);
};

// ===========================
// DATA FLOW RELATIONSHIPS
// ===========================

/**
 * Defines how data flows through the ATS system:
 * Jobs → Candidates → New Hires → FLM
 *
 * - Jobs are created first
 * - Candidates are assigned to Jobs
 * - When a Candidate is Hired, they become a New Hire
 * - New Hires complete FLM documentation
 */

export const ATS_DATA_FLOW = {
  JOBS: 'Jobs are the starting point - requisitions for positions',
  CANDIDATES: 'Candidates are assigned to Jobs and progress through stages',
  NEW_HIRES: 'Hired Candidates become New Hires for onboarding',
  FLM: 'New Hires complete FLM (First Line Manager) documentation'
};

// ===========================
// FIELD VALIDATION
// ===========================

export const FIELD_VALIDATIONS = {
  JOB_TITLE: {
    required: true,
    maxLength: 255,
    minLength: 2
  },
  CANDIDATE_NAME: {
    required: true,
    maxLength: 255,
    minLength: 2
  },
  EMAIL: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    maxLength: 255
  },
  PHONE: {
    pattern: /^[\d\s\-+()]+$/,
    maxLength: 50
  },
  COMPENSATION: {
    min: 0,
    max: 9999999.99
  }
};

const ATS_CONSTANTS = {
  JOB_STATUS,
  JOB_STATUS_OPTIONS,
  PRIORITY,
  PRIORITY_OPTIONS,
  DEPARTMENT,
  DEPARTMENT_OPTIONS,
  CANDIDATE_STATUS,
  CANDIDATE_STATUS_OPTIONS,
  RECRUITMENT_SOURCE,
  RECRUITMENT_SOURCE_OPTIONS,
  INTERVIEW_TYPE,
  INTERVIEW_TYPE_OPTIONS,
  NEW_HIRE_STATUS,
  NEW_HIRE_STATUS_OPTIONS,
  FLM_STATUS,
  FLM_STATUS_OPTIONS,
  OFFER_STATUS,
  OFFER_STATUS_OPTIONS,
  EMPLOYMENT_TYPE,
  EMPLOYMENT_TYPE_OPTIONS,
  REJECTION_REASON,
  REJECTION_REASON_OPTIONS,
  getCandidateStatusColor,
  getPriorityColor,
  isJobActive,
  canAssignCandidate,
  isCandidateHired,
  getNextCandidateStatus,
  isValidPriority,
  isValidJobStatus,
  isValidCandidateStatus,
  ATS_DATA_FLOW,
  FIELD_VALIDATIONS
};

export default ATS_CONSTANTS;